(() => {
  if (window.TabbedVideosSection) return;

  class TabbedVideosSection {
    constructor(root) {
      this.root = root;
      this.tabs = Array.from(root.querySelectorAll('[data-tabbed-videos-tab]'));
      this.panels = Array.from(root.querySelectorAll('[data-tabbed-videos-panel]'));
      this.dialog = root.querySelector('[data-tabbed-videos-dialog]');
      this.dialogMedia = root.querySelector('[data-tabbed-videos-dialog-media]');
      this.swipers = new Map();
      this.abortController = new AbortController();
      this.signal = this.abortController.signal;
      this.handleKeydown = this.handleKeydown.bind(this);
      this.handleDialogCancel = this.handleDialogCancel.bind(this);
      this.init();
    }

    init() {
      this.panels.forEach((panel) => this.initSwiper(panel));

      this.tabs.forEach((tab) => {
        tab.addEventListener('click', () => this.activateTab(tab.dataset.tabbedVideosTab), { signal: this.signal });
        tab.addEventListener('keydown', this.handleKeydown, { signal: this.signal });
      });

      this.root.querySelectorAll('[data-tabbed-videos-trigger]').forEach((trigger) => {
        trigger.addEventListener('click', () => this.openVideo(trigger), { signal: this.signal });
      });

      this.root.querySelectorAll('[data-tabbed-videos-close]').forEach((button) => {
        button.addEventListener('click', () => this.closeVideo(), { signal: this.signal });
      });

      if (this.dialog) {
        this.dialog.addEventListener('cancel', this.handleDialogCancel, { signal: this.signal });
        this.dialog.addEventListener(
          'close',
          () => {
            this.clearVideo();
            document.body.classList.remove('tabbed-videos-modal-open');
          },
          { signal: this.signal }
        );
      }
    }

    initSwiper(panel) {
      const swiperElement = panel.querySelector('[data-tabbed-videos-swiper]');
      if (!swiperElement || typeof window.Swiper !== 'function') return;

      const swiper = new window.Swiper(swiperElement, {
        slidesPerView: 'auto',
        spaceBetween: 12,
        watchOverflow: true,
        observer: true,
        observeParents: true,
        pagination: {
          el: panel.querySelector('[data-tabbed-videos-pagination]'),
          type: 'progressbar'
        },
        navigation: {
          prevEl: panel.querySelector('[data-tabbed-videos-prev]'),
          nextEl: panel.querySelector('[data-tabbed-videos-next]')
        },
        breakpoints: {
          750: {
            spaceBetween: 20
          },
          990: {
            spaceBetween: 24
          }
        }
      });

      this.swipers.set(panel.dataset.tabbedVideosPanel, swiper);
    }

    activateTab(platform) {
      this.tabs.forEach((tab) => {
        const active = tab.dataset.tabbedVideosTab === platform;
        tab.classList.toggle('is-active', active);
        tab.setAttribute('aria-selected', active ? 'true' : 'false');
        tab.tabIndex = active ? 0 : -1;
      });

      this.panels.forEach((panel) => {
        const active = panel.dataset.tabbedVideosPanel === platform;
        panel.classList.toggle('is-active', active);
        panel.hidden = !active;
      });

      requestAnimationFrame(() => {
        const swiper = this.swipers.get(platform);
        if (swiper && !swiper.destroyed) swiper.update();
      });
    }

    handleKeydown(event) {
      if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;

      event.preventDefault();
      const currentIndex = this.tabs.indexOf(event.currentTarget);
      let nextIndex = currentIndex;

      if (event.key === 'ArrowLeft') nextIndex = (currentIndex - 1 + this.tabs.length) % this.tabs.length;
      if (event.key === 'ArrowRight') nextIndex = (currentIndex + 1) % this.tabs.length;
      if (event.key === 'Home') nextIndex = 0;
      if (event.key === 'End') nextIndex = this.tabs.length - 1;

      const nextTab = this.tabs[nextIndex];
      nextTab.focus();
      this.activateTab(nextTab.dataset.tabbedVideosTab);
    }

    openVideo(trigger) {
      if (!this.dialog || !this.dialogMedia || trigger.disabled) return;

      const provider = trigger.dataset.videoProvider;
      const videoTitle = trigger.dataset.videoTitle || 'Video';
      const iframe = document.createElement('iframe');
      let source = '';

      if (provider === 'youtube' && trigger.dataset.videoId) {
        source = `https://www.youtube.com/embed/${encodeURIComponent(trigger.dataset.videoId)}?autoplay=1&rel=0`;
      } else if (provider === 'vimeo' && trigger.dataset.videoId) {
        source = `https://player.vimeo.com/video/${encodeURIComponent(trigger.dataset.videoId)}?autoplay=1`;
      } else if (provider === 'tiktok') {
        const videoId = this.getTikTokVideoId(trigger.dataset.videoUrl || '');
        if (videoId) source = `https://www.tiktok.com/player/v1/${encodeURIComponent(videoId)}?autoplay=1`;
      }

      if (!source) return;

      iframe.src = source;
      iframe.title = videoTitle;
      iframe.allow = 'autoplay; encrypted-media; picture-in-picture; fullscreen';
      iframe.allowFullscreen = true;
      iframe.referrerPolicy = 'strict-origin-when-cross-origin';
      this.dialogMedia.classList.toggle('is-tiktok', provider === 'tiktok');
      this.dialogMedia.replaceChildren(iframe);
      document.body.classList.add('tabbed-videos-modal-open');
      this.dialog.showModal();
    }

    getTikTokVideoId(url) {
      const match = url.match(/\/video\/(\d+)/);
      return match ? match[1] : '';
    }

    handleDialogCancel(event) {
      event.preventDefault();
      this.closeVideo();
    }

    closeVideo() {
      if (this.dialog && this.dialog.open) this.dialog.close();
    }

    clearVideo() {
      if (!this.dialogMedia) return;
      this.dialogMedia.replaceChildren();
      this.dialogMedia.classList.remove('is-tiktok');
    }

    destroy() {
      this.abortController.abort();
      this.swipers.forEach((swiper) => {
        if (swiper && !swiper.destroyed) swiper.destroy(true, true);
      });
      this.swipers.clear();
      this.closeVideo();
      this.root.tabbedVideosInstance = null;
    }
  }

  const initialize = (scope = document) => {
    const roots = [];
    if (scope.matches && scope.matches('[data-tabbed-videos]')) roots.push(scope);
    if (scope.querySelectorAll) roots.push(...scope.querySelectorAll('[data-tabbed-videos]'));

    roots.forEach((root) => {
      if (root.tabbedVideosInstance) return;
      root.tabbedVideosInstance = new TabbedVideosSection(root);
    });
  };

  window.TabbedVideosSection = TabbedVideosSection;
  initialize();

  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    const root = event.target.querySelector('[data-tabbed-videos]');
    if (root && root.tabbedVideosInstance) root.tabbedVideosInstance.destroy();
  });
})();
